import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

/**
 * useAppOpenTracker — Phase 47 · Feb 2026
 * ------------------------------------------------------------------
 * Fires a single POST /api/telemetry/app-open per browser session
 * recording whether Nymia is running as a PWA (standalone) or in a
 * regular browser tab. Fire-and-forget: never blocks render, never
 * throws to the UI.
 *
 * Session dedupe uses sessionStorage — a fresh tab produces exactly
 * one event even if this hook re-mounts (e.g. after login).
 *
 * Anonymous identity uses a UUID stored in localStorage under
 * `nymia_anon_id`. Not a fingerprint, just a stable-per-device token
 * so admins can distinguish returning anonymous visitors from unique
 * ones. Auth users provide their JWT (via the API client's default
 * headers) so the backend can attach the event to their user_id.
 */

const SESSION_KEY = 'nymia_app_open_sent';
const ANON_ID_KEY = 'nymia_anon_id';

const detectDisplayMode = () => {
  if (typeof window === 'undefined') return 'unknown';
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return 'standalone';
    if (window.navigator?.standalone === true) return 'standalone';
    return 'browser';
  } catch (_e) { return 'unknown'; }
};

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const getOrCreateAnonId = () => {
  try {
    let id = window.localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      // crypto.randomUUID is supported in every browser we care about
      // (Chrome 92+, Safari 15.4+, Firefox 95+). Fallback to Math.random.
      id = window.crypto?.randomUUID?.() ||
        `anon_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      window.localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch (_e) {
    return `anon_ephemeral_${Math.random().toString(36).slice(2)}`;
  }
};

const getOrCreateSessionId = () => {
  try {
    let id = window.sessionStorage.getItem('nymia_session_id');
    if (!id) {
      id = window.crypto?.randomUUID?.() ||
        `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      window.sessionStorage.setItem('nymia_session_id', id);
    }
    return id;
  } catch (_e) {
    return `sess_ephemeral_${Math.random().toString(36).slice(2)}`;
  }
};

export const useAppOpenTracker = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;

    // Wait a beat so the initial render + hydration finish first —
    // telemetry must NEVER block above-the-fold rendering.
    const t = window.setTimeout(async () => {
      if (cancelled) return;
      try {
        // Dedupe per session. Note: if the user logs in mid-session
        // the anon record is already there — the backend will use its
        // upsert semantics and simply keep the first row. That's fine
        // for this metric (we care about opens, not identities).
        const alreadySent = window.sessionStorage.getItem(SESSION_KEY);
        if (alreadySent) return;
        window.sessionStorage.setItem(SESSION_KEY, '1');

        const payload = {
          display_mode: detectDisplayMode(),
          platform: detectPlatform(),
          session_id: getOrCreateSessionId(),
          anon_id: user ? null : getOrCreateAnonId(),
        };
        await api.post('/telemetry/app-open', payload);
      } catch (_e) {
        // Silent — telemetry MUST NOT surface errors to the UI.
      }
    }, 1500);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // We intentionally re-run when auth changes so a login within the
    // same tab attributes the event to the user_id (still dedupe-safe
    // — the backend upserts by session_id).
  }, [user?.id]);
};

export default useAppOpenTracker;
